import {
  FitBoundsOptions,
  GeoJSONSourceSpecification,
  LngLatLike,
  Map as MlMap,
} from 'maplibre-gl';
import { Country } from '@/types/geocode';

export interface MapData {
  name: string;
  existingMapId?: string;
  isIsolated?: boolean;
}

export type Coordinate = [number, number];
export type Coordinates = [number, number][];

// TODO: Replace with types/geojson.ts
export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: Coordinates[];
}

// TODO: Replace with types/geojson.ts
export interface GeoJSONMultiPolygon {
  type: 'MultiPolygon';
  coordinates: Coordinates[][];
}

// TODO: Replace with types/geojson.ts
export interface GeoJSONLineString {
  type: 'LineString';
  coordinates: Coordinates;
}

export enum BountyCollectionType {
  App = 'app',
  Dashcam = 'dashcam',
  Drone = 'drone',
}

export interface Bounty {
  _id: string;
  open: boolean;
  type?: BountyCollectionType;
  closedReason?: string;
}

export enum RegionType {
  City = 'city',
  County = 'county',
  MSA = 'msa',
  Province = 'province',
  Special = 'special',
  State = 'state',
  Territory = 'territory',
}

export interface Region {
  _id: string;
  name: string;
  country: Country;
  type: RegionType;
  bounty?: Bounty;
  active?: boolean;
  disabled?: boolean;
  geojson: GeoJSONPolygon | GeoJSONMultiPolygon;
}

export interface CoverageReport {
  _doc: {
    createdAt: Date;
    updatedAt: Date;
    start: Date;
    end: Date;
    path: GeoJSONLineString;
    frames?: any;
  };
  path?: GeoJSONLineString;
  frames?: any;
  error?: string;
}

export interface CoverageFilter {
  dashcam: ContributionsFilter;
  app: ContributionsFilter;
  yours: ContributionsFilter;
  regions?: boolean;
  from?: Date;
  to?: Date;
}

export enum ContributionsFilter {
  All = 'all',
  Today = 'today',
  Yesterday = 'yesterday',
  Week = 'week',
  Month = 'month',
  None = '',
}

export interface ContributorMappedTiles {
  [ContributionsFilter.All]: number;
  [ContributionsFilter.Today]: number;
  [ContributionsFilter.Yesterday]: number;
  [ContributionsFilter.Week]: number;
  [ContributionsFilter.Month]: number;
}

export interface Point {
  latitude: number;
  longitude: number;
}

export interface MapViewState extends Point {
  zoom: number;
  width: number;
}

export interface H3ApiData {
  attributions: H3Tile[];
}

export interface FlightPathApiData {
  missionUrls: string[];
}

export interface H3Tile {
  h3Id: string;
  username: string;
}

export type IMapType =
  | 'dark'
  | 'light'
  | 'satellite'
  | 'aiTrainers'
  | 'valve'
  | 'location-monitoring'
  | 'coverage';

export enum IMapProvider {
  Mapbox = 'mapbox',
  Apple = 'apple',
}

export interface IPoint {
  lat: number;
  lon: number;
  image?: string;
  yaw?: number;
}

export interface ITarget extends IPoint {
  azimuth?: number;
}

export type IPoints = IPoint[];

export interface IDisplayOptions {
  hasColorDots?: boolean;
  hasAppleMaps?: boolean;
  isTargetMarkerDisabled?: boolean;
  selectedCameraIndex?: number;
  alignViewBasedOnPoints?: boolean;
  hideTargetMarker?: boolean;
  initialMapProvider?: IMapProvider;
}

export enum EventTypes {
  CLICK = 'click',
  MOUSE_ENTER = 'mouseenter',
  MOUSE_LEAVE = 'mouseleave',
}

export interface InitializationParams {
  mapContainer: HTMLDivElement;
  onLoadCallback: (newMap: MlMap) => void;
  zoom?: number;
  center?: LngLatLike;
  bounds?: [LngLatLike, LngLatLike];
  options?: FitBoundsOptions;
  style?: string;
}

export type SourceTypes =
  | 'geojson'
  | 'raster'
  | 'raster-dem'
  | 'vector'
  | 'image'
  | 'video';

export type LayerTypes =
  | 'symbol'
  | 'fill'
  | 'circle'
  | 'line'
  | 'heatmap'
  | 'fill-extrusion'
  | 'raster'
  | 'hillshade'
  | 'background';

export type SourceOptions = {
  id: string;
  options: GeoJSONSourceSpecification;
};

export interface IMapOptions {
  enableStyles?: boolean;
  enableLocation?: boolean;
  enableSearch?: boolean;
  enableSearchMarker?: boolean;
  enableExpandMap?: boolean;
  rememberLocation?: boolean;
  maxZoom?: number;
  showMapboxInfo?: boolean;
  showCoverageReference?: boolean;
  enableInteraction?: boolean;
  showCoverageStats?: boolean;
  extendedControlsClassName?: string;
}
